import { Button, Grid2 as Grid, FormGroup, FormControlLabel, Checkbox, Fade } from "@mui/material";
import * as React from "react";
import { connect } from "react-redux";
import SharedMaterialReactTable, { ISharedTableColumn } from "../../Shared/MaterialReactTable";
import { Theme, createStyles } from '@mui/material/styles';
import { withStyles, WithStyles } from '@mui/styles';
import idssStyle from "../../../Configuration/SharedStyling";
import { IFilesUploadReportPayload } from "../../../DataClasses/ReportData";
import {
    IGenericUserProps,
    genericMapStateToProps
} from "../../../DataClasses/GenericUser";
import ReportsService from "../../Services/ReportsService";
import { AjaxError } from "rxjs/ajax";
import { firstValueFrom } from "rxjs";
import { Link, LinkProps } from "react-router-dom";
import BackNavigation from '../../Shared/BackNavigation';
import * as AuthorizationPolicies from "../../../Configuration/AuthorizationPolicies";
import { Redirect } from "react-router-dom";

const styles = (theme: Theme) => createStyles({ ...idssStyle(theme) });

interface IFilesUploadReportState {
    data: any[];
    isLoading: boolean;
    isSysAdmin: boolean;
    showAllItems: boolean;
    redirect: boolean
}

// The usage of React.forwardRef will no longer be required for react-router-dom v6.
// see https://github.com/ReactTraining/react-router/issues/6056

type IStylesProps = WithStyles<typeof styles>;
const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>(
    (props, ref) => <Link innerRef={ref as any} {...props} />
);

// VIEW
class FilesUploadReportView extends React.Component<
    IGenericUserProps & IStylesProps,
    IFilesUploadReportState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            data: [],
            isLoading: true,
            isSysAdmin: AuthorizationPolicies.IsGlobalAdmin(this.props.user),
            showAllItems: true,
            redirect: false
        };
    }

    public componentDidMount() {
        this.RefreshModel(this.state.showAllItems);
    }

    private redirectToErrorPage() {
        return <Redirect to='/errorpage' />;
    }

    private setRedirectState() {
        this.setState({ redirect: true });
    }

    private subIdBodyTemplate = (rowData: any) => {
        const { classes } = this.props;
        return (
            <Button
                to={"submissionHome/" + rowData.subId + "/" + rowData.component.replace("HEDIS", "DST")}
                className={classes.linkButton}
                component={AdapterLink}
            >
                {rowData.subId}
            </Button>
        );
    };

    private getTableColumns = (): ISharedTableColumn<any>[] => [
        {
            accessorKey: 'orgId',
            header: 'Org ID',
            enableSorting: true,
            enableFiltering: true,
            size: 100
        },
        {
            accessorKey: 'subId',
            header: 'Sub ID',
            enableSorting: true,
            enableFiltering: true,
            size: 120,
            Cell: ({ row }) => this.subIdBodyTemplate(row.original)
        },
        {
            accessorKey: 'orgName',
            header: 'Org Name',
            enableSorting: true,
            enableFiltering: true,
            size: 200
        },
        {
            accessorKey: 'component',
            header: 'Component',
            enableSorting: true,
            enableFiltering: true,
            size: 120
        },
        {
            accessorKey: 'fileName',
            header: 'FileName',
            enableSorting: true,
            enableFiltering: false,
            size: 200
        },
        {
            accessorKey: 'validationStatus',
            header: 'ValidationStatus',
            enableSorting: true,
            enableFiltering: true,
            size: 150
        },
        {
            accessorKey: 'createUser',
            header: 'CreateUser',
            enableSorting: true,
            enableFiltering: true,
            size: 150
        },
        {
            accessorKey: 'createDateTime',
            header: 'CreateDateTime',
            enableSorting: true,
            enableFiltering: false,
            size: 150
        }
    ];

    public render() {
        if (this.state.redirect) {
            return this.redirectToErrorPage();
        }
        const { classes } = this.props;

        return (
            <Grid container={true} className={classes.gridRowFlex} spacing={2}>
                <Grid size={12}>
                    <Grid container={true} spacing={2}>
                        <Grid size={12}>
                            <BackNavigation text="Back to Reports" href="/reportspage" />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <div className={classes.card}>
                        {this.state.isSysAdmin ? <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={this.state.showAllItems}
                                        onChange={async () => this.toggleRefreshModel(!this.state.showAllItems)}
                                        color="primary"
                                    />
                                }
                                label="Show All Submissions"
                            />
                        </FormGroup> : <div />}
                        <Fade in={!this.state.isLoading}><div>
                            <SharedMaterialReactTable
                                data={this.state.data}
                                columns={this.getTableColumns()}
                                title="Uploaded Files"
                                isLoading={this.state.isLoading}
                                enablePagination={true}
                                enableSorting={true}
                                enableFiltering={true}
                                enableColumnActions={true}
                                enableRowActions={false}
                                initialState={{
                                    pagination: { pageIndex: 0, pageSize: 20 },
                                }}
                                muiPaginationProps={{
                                    rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                                }}
                                emptyMessage="No files found."
                            />
                        </div></Fade>
                    </div>
                </Grid>
            </Grid >
        );
    }

    private async toggleRefreshModel(showAll: boolean) {
        this.setState({ isLoading: true });
        await this.RefreshModel(showAll);
        this.setState({ showAllItems: showAll })
        this.setState({ isLoading: false });
    }

    private async RefreshModel(showAllItems: boolean) {
        this.setState({ isLoading: true });
        const retval: IFilesUploadReportPayload[] | AjaxError = await firstValueFrom(
            ReportsService.GetFilesUploadReportCaller$(
                this.props.user,
                showAllItems
            )
        );
        if (retval instanceof AjaxError || !retval) {
            this.setRedirectState();
        } else {
            this.setState({
                data: retval.map(item => ({
                    orgId: item.organizationId,
                    subId: item.submissionId,
                    orgName: item.organizationName,
                    component: item.component,
                    fileName: item.savedName,
                    validationStatus: item.validationStatus,
                    createUser: item.createUser,
                    createDateTime: item.createDate
                }))
            });
        }
        this.setState({ isLoading: false });
    }
}

// REDUX CONTAINER
const FilesUploadReportPageWithState = connect<IGenericUserProps>(
    genericMapStateToProps
)(FilesUploadReportView);

// STYLES CONTAINER
const FilesUploadReportPage = withStyles(styles)(
    FilesUploadReportPageWithState
);

export default FilesUploadReportPage;
